<template>
  <div class="xero-settings-wrapper">
    <!-- SIDEBAR -->
    <Sidebar />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <div class="topbar">
        <h1 class="page-title">🔄 Xero Integration</h1>
        <div class="topbar-right">
          <span class="welcome-text">Welcome back!</span>
          <button class="btn-logout" @click="logout">Logout</button>
        </div>
      </div>

      <!-- CONTENT -->
      <div class="content-wrapper">

    <!-- Loading State -->
    <div v-if="loading" class="loading-card">
      <div class="spinner"></div>
      <p>Checking Xero connection...</p>
    </div>

    <!-- Not Connected State -->
    <div v-else-if="!isConnected" class="connect-card">
      <div class="card-header">
        <div class="xero-logo">
          <svg width="120" height="40" viewBox="0 0 283 70" fill="none">
            <path d="M13.6 70C6.1 70 0 63.9 0 56.4V13.6C0 6.1 6.1 0 13.6 0h56.8C77.9 0 84 6.1 84 13.6v42.8C84 63.9 77.9 70 70.4 70H13.6z" fill="#13B5EA"/>
            <path d="M54.7 33.8L68.1 20.4c.9-.9.9-2.4 0-3.3l-3.3-3.3c-.9-.9-2.4-.9-3.3 0L48.1 27.2c-.9.9-2.4.9-3.3 0L31.4 13.8c-.9-.9-2.4-.9-3.3 0l-3.3 3.3c-.9.9-.9 2.4 0 3.3l13.4 13.4c.9.9.9 2.4 0 3.3L24.8 50.5c-.9.9-.9 2.4 0 3.3l3.3 3.3c.9.9 2.4.9 3.3 0l13.4-13.4c.9-.9 2.4-.9 3.3 0l13.4 13.4c.9.9 2.4.9 3.3 0l3.3-3.3c.9-.9.9-2.4 0-3.3L54.7 37.1c-.9-.9-.9-2.4 0-3.3z" fill="#fff"/>
          </svg>
        </div>
        <h2>Connect to Xero</h2>
        <p>Automatically push month-end reports to Xero as draft invoices</p>
      </div>

      <div class="features-list">
        <div class="feature-item">
          <span class="check-icon">✓</span>
          <span>Automatic invoice creation from monthly reports</span>
        </div>
        <div class="feature-item">
          <span class="check-icon">✓</span>
          <span>Correct VAT calculation (20% on fees, 0% on disbursements)</span>
        </div>
        <div class="feature-item">
          <span class="check-icon">✓</span>
          <span>Contact auto-creation for property owners</span>
        </div>
        <div class="feature-item">
          <span class="check-icon">✓</span>
          <span>All invoices created as DRAFT for your review</span>
        </div>
      </div>

      <button @click="connectToXero" class="btn-connect" :disabled="connecting">
        <span v-if="!connecting">Connect to Xero</span>
        <span v-else>Connecting...</span>
      </button>

      <p class="help-text">
        You will be redirected to Xero to authorize the connection
      </p>
    </div>

    <!-- Connected State -->
    <div v-else class="connected-card">
      <div class="status-header">
        <div class="status-badge green">
          <span class="badge-icon">✓</span>
          <span>Connected to Xero</span>
        </div>
      </div>

      <div class="connection-details">
        <div class="detail-row">
          <span class="label">Tenant ID:</span>
          <span class="value">{{ connectionData.tenant_id }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Connected since:</span>
          <span class="value">{{ formatDate(connectionData.connected_at) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Token expires:</span>
          <span class="value">{{ formatDate(connectionData.expires_at) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Status:</span>
          <span class="value status-active">
            <span class="status-dot"></span>
            Active
          </span>
        </div>
      </div>

      <div class="action-buttons">
        <button @click="refreshStatus" class="btn-secondary" :disabled="loading">
          <span v-if="!loading">🔄 Refresh Status</span>
          <span v-else>Refreshing...</span>
        </button>
        <button @click="disconnectXero" class="btn-disconnect" :disabled="disconnecting">
          <span v-if="!disconnecting">Disconnect Xero</span>
          <span v-else>Disconnecting...</span>
        </button>
      </div>

      <div class="info-box success">
        <div class="info-icon">✅</div>
        <div class="info-content">
          <strong>Xero is connected!</strong>
          <p>You can now push monthly reports to Xero from the Month-End screen.</p>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="errorMessage" class="error-box">
      <div class="error-icon">⚠️</div>
      <div class="error-content">
        <strong>Error</strong>
        <p>{{ errorMessage }}</p>
      </div>
      <button @click="errorMessage = ''" class="close-error">×</button>
    </div>

    <!-- Modal Success -->
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="showSuccessModal = false">
      <div class="modal-success">
        <div class="modal-header-success">
          <div class="checkmark-circle">
            <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
              <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
          <h3 class="modal-title-success">{{ successMessage }}</h3>
        </div>

        <div class="modal-footer-success">
          <button @click="showSuccessModal = false" class="btn-modal-ok">OK</button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmation -->
    <div v-if="showConfirmModal" class="modal-overlay" @click.self="showConfirmModal = false">
      <div class="modal-confirm">
        <div class="modal-header-confirm">
          <div class="warning-icon">⚠️</div>
          <h3 class="modal-title-confirm">Confirm Action</h3>
          <p class="modal-message-confirm">{{ confirmMessage }}</p>
        </div>

        <div class="modal-footer-confirm">
          <button @click="showConfirmModal = false" class="btn-modal-cancel">Cancel</button>
          <button @click="confirmAction" class="btn-modal-confirm">Confirm</button>
        </div>
      </div>
    </div>

    </div><!-- END content-wrapper -->
    </main><!-- END main -->
  </div><!-- END xero-settings-wrapper -->
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'XeroSettingsView',

  components: {
    Sidebar
  },

  data() {
    return {
      isConnected: false,
      loading: true,
      connecting: false,
      disconnecting: false,
      errorMessage: '',
      connectionData: {
        tenant_id: '',
        connected_at: null,
        expires_at: null
      },
      showSuccessModal: false,
      showConfirmModal: false,
      successMessage: '',
      confirmMessage: '',
      confirmAction: null
    };
  },

  computed: {
    baseUrl() {
      // Use same logic as settings.js for consistency
      if (window.location.pathname.startsWith('/dev')) {
        return window.location.origin + '/dev';
      }
      return window.location.origin;
    }
  },

  methods: {
    async checkConnectionStatus() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${this.baseUrl}/api/pms/xero-status/`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success && response.data.data.connected) {
          this.isConnected = true;
          this.connectionData = response.data.data;
        } else {
          this.isConnected = false;
        }

      } catch (error) {
        console.error('Error checking Xero status:', error);
        this.errorMessage = 'Failed to check Xero connection status';
      } finally {
        this.loading = false;
      }
    },

    async connectToXero() {
      this.connecting = true;
      this.errorMessage = '';

      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${this.baseUrl}/api/pms/xero-connect/`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          // Redirect to Xero authorization URL
          window.location.href = response.data.data.authorization_url;
        } else {
          throw new Error(response.data.message || 'Failed to connect to Xero');
        }

      } catch (error) {
        console.error('Error connecting to Xero:', error);
        this.errorMessage = error.response?.data?.message || 'Failed to start Xero connection. Please try again.';
        this.connecting = false;
      }
    },

    disconnectXero() {
      this.confirmMessage = 'Are you sure you want to disconnect Xero?\n\nYou will no longer be able to push reports to Xero.';
      this.confirmAction = this.performDisconnect;
      this.showConfirmModal = true;
    },

    async performDisconnect() {
      this.showConfirmModal = false;
      this.disconnecting = true;
      this.errorMessage = '';

      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/xero-disconnect/`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          this.isConnected = false;
          this.connectionData = {
            tenant_id: '',
            connected_at: null,
            expires_at: null
          };
          this.successMessage = 'Xero disconnected successfully';
          this.showSuccessModal = true;
        } else {
          throw new Error(response.data.message || 'Failed to disconnect');
        }

      } catch (error) {
        console.error('Error disconnecting Xero:', error);
        this.errorMessage = error.response?.data?.message || 'Failed to disconnect Xero. Please try again.';
      } finally {
        this.disconnecting = false;
      }
    },

    async refreshStatus() {
      await this.checkConnectionStatus();
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    },

    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/login');
    }
  },

  mounted() {
    // Check if redirected back from Xero with success
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('connected') === 'true') {
      // Remove query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Show success and check status
      setTimeout(() => {
        this.successMessage = 'Xero connected successfully!';
        this.showSuccessModal = true;
        this.checkConnectionStatus();
      }, 500);
    } else if (urlParams.get('error')) {
      const error = urlParams.get('error');
      this.errorMessage = `Connection failed: ${error}`;
      window.history.replaceState({}, document.title, window.location.pathname);
      this.loading = false;
    } else {
      this.checkConnectionStatus();
    }
  }
};
</script>

<style scoped>
/* ===================================
   MAIN LAYOUT (Sidebar + Main)
   =================================== */
.xero-settings-wrapper {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 260px; /* Sidebar width */
  min-height: 100vh;
}

/* Topbar */
.topbar {
  background: white;
  border-bottom: 1px solid #ebe9f8;
  padding: 0 28px;
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 50;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.welcome-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.btn-logout {
  padding: 8px 16px;
  background: #ef4444;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

/* Content Wrapper */
.content-wrapper {
  flex: 1;
  padding: 40px 32px;
  overflow-y: auto;
}

/* Loading */
.loading-card {
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top-color: #13B5EA;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Connect Card */
.connect-card,
.connected-card {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.card-header {
  text-align: center;
  margin-bottom: 40px;
}

.xero-logo {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.card-header h2 {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 10px 0;
}

.card-header p {
  font-size: 15px;
  color: #718096;
  margin: 0;
}

/* Features */
.features-list {
  margin: 30px 0 40px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 0;
  font-size: 15px;
  color: #2d3748;
  border-bottom: 1px solid #e2e8f0;
}

.feature-item:last-child {
  border-bottom: none;
}

.check-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #10b981;
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
}

/* Buttons */
.btn-connect,
.btn-secondary,
.btn-disconnect {
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-connect {
  width: 100%;
  background: #13B5EA;
  color: white;
}

.btn-connect:hover:not(:disabled) {
  background: #1098cc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(19, 181, 234, 0.4);
}

.btn-connect:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.help-text {
  text-align: center;
  font-size: 13px;
  color: #a0aec0;
  margin: 15px 0 0 0;
}

/* Connected State */
.status-header {
  margin-bottom: 30px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 30px;
  font-size: 15px;
  font-weight: 600;
}

.status-badge.green {
  background: #d1fae5;
  color: #065f46;
}

.badge-icon {
  font-size: 18px;
}

.connection-details {
  background: #f7fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  font-size: 14px;
  color: #718096;
  font-weight: 500;
}

.detail-row .value {
  font-size: 14px;
  color: #2d3748;
  font-weight: 600;
  max-width: 60%;
  text-align: right;
  word-break: break-all;
}

.status-active {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #10b981 !important;
}

.status-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.btn-secondary {
  flex: 1;
  background: #e2e8f0;
  color: #2d3748;
}

.btn-secondary:hover:not(:disabled) {
  background: #cbd5e0;
}

.btn-disconnect {
  flex: 1;
  background: #ef4444;
  color: white;
}

.btn-disconnect:hover:not(:disabled) {
  background: #dc2626;
}

/* Info Box */
.info-box {
  display: flex;
  gap: 15px;
  padding: 20px;
  border-radius: 12px;
  margin-top: 20px;
}

.info-box.success {
  background: #ecfdf5;
  border: 2px solid #10b981;
}

.info-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.info-content strong {
  display: block;
  font-size: 16px;
  color: #065f46;
  margin-bottom: 5px;
}

.info-content p {
  font-size: 14px;
  color: #047857;
  margin: 0;
  line-height: 1.5;
}

/* Error Box */
.error-box {
  max-width: 800px;
  margin: 0 auto 20px;
  background: #fef2f2;
  border: 2px solid #ef4444;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  gap: 15px;
  position: relative;
}

.error-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.error-content strong {
  display: block;
  font-size: 16px;
  color: #991b1b;
  margin-bottom: 5px;
}

.error-content p {
  font-size: 14px;
  color: #b91c1c;
  margin: 0;
}

.close-error {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  color: #991b1b;
  cursor: pointer;
  padding: 5px 10px;
}

.close-error:hover {
  color: #7f1d1d;
}

/* ============= MODALS ============= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Modal Success */
.modal-success {
  background: white;
  border-radius: 16px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header-success {
  padding: 40px 32px 24px;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  position: relative;
}

.checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #10b981;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #10b981;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.checkmark-circle-bg {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke-miterlimit: 10;
  stroke: #10b981;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: #10b981;
  stroke-width: 3;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px #10b981;
  }
}

.modal-title-success {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: 'DM Sans', sans-serif;
}

.modal-footer-success {
  padding: 20px 32px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
}

.btn-modal-ok {
  padding: 12px 32px;
  border: none;
  background: #13B5EA;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
  min-width: 120px;
}

.btn-modal-ok:hover {
  background: #0e9ad1;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(19, 181, 234, 0.3);
}

/* Modal Confirmation */
.modal-confirm {
  background: white;
  border-radius: 16px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header-confirm {
  padding: 40px 32px 24px;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.warning-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.modal-title-confirm {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  font-family: 'DM Sans', sans-serif;
}

.modal-message-confirm {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
  white-space: pre-line;
}

.modal-footer-confirm {
  padding: 20px 32px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.btn-modal-cancel {
  padding: 12px 24px;
  border: 1.5px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-cancel:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.btn-modal-confirm {
  padding: 12px 24px;
  border: none;
  background: #ef4444;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-confirm:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===================================
   RESPONSIVE
   =================================== */
@media (max-width: 1024px) {
  .main {
    margin-left: 0;
  }

  .topbar {
    padding: 16px 20px;
  }

  .content-wrapper {
    padding: 24px 20px;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 18px;
  }

  .topbar-right {
    gap: 12px;
  }

  .welcome-text {
    display: none;
  }

  .btn-logout {
    padding: 6px 12px;
    font-size: 13px;
  }

  .connect-card,
  .connected-card {
    padding: 24px 20px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-secondary,
  .btn-disconnect {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .content-wrapper {
    padding: 20px 16px;
  }

  .page-title {
    font-size: 16px;
  }
}
</style>
